/* WiserTrail 2026-10-20: progressive UI enhancement only.
 * Does not fetch map content, grant access, change tiers, persist selections,
 * change prices, send analytics, or loosen iframe permissions. */
(() => {
  'use strict';
  if (window.WTMapPolish) return;
  const configNode = document.querySelector('[data-wtmap-config]');
  if (!configNode) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch { return; }
  const text = config.messages || {};
  const roots = new WeakSet(), frames = new WeakSet(), documents = new WeakSet();
  const own = (node, selector) => node.querySelector(selector);
  const all = (node, selector) => [...node.querySelectorAll(selector)];
  const rootSelectors = '[data-wtams-map],[data-wtcity-map],[data-free-map],.wtplus,.wtcityplus,.wtamsplus,.wtparisplus';
  const cardSelector = '.card,.wt-free-card';
  const make = (doc, tag, cls, content) => {
    const node = doc.createElement(tag);
    if (cls) node.className = cls;
    if (content !== undefined) node.textContent = content;
    return node;
  };
  const button = (doc, label, fn) => {
    const node = make(doc, 'button', '', label); node.type = 'button';
    node.addEventListener('click', fn); return node;
  };
  function stylesheet(doc) {
    if (!config.css || doc.querySelector('link[data-wtmap-style]')) return;
    const link = doc.createElement('link'); link.rel = 'stylesheet';
    link.href = config.css; link.dataset.wtmapStyle = '1'; doc.head.appendChild(link);
  }
  function focusAndScroll(node) {
    if (!node) return;
    const view = node.ownerDocument.defaultView;
    node.scrollIntoView({block:'start',behavior:view.matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'});
    if (!node.matches('input,select,textarea,button,a,[tabindex]')) node.tabIndex = -1;
    node.focus({preventScroll:true});
  }
  function decorate(root) {
    all(root, cardSelector).forEach(card => {
      const name = own(card, 'h2,h3')?.textContent.trim() || '';
      all(card, 'button[data-save]').forEach(control => {
        if (!control.hasAttribute('aria-label')) control.setAttribute('aria-label', `${text.save || 'Save'}: ${name}`);
        if (!control.hasAttribute('aria-pressed') || control.dataset.wtmapPressed) { control.dataset.wtmapPressed='1'; control.setAttribute('aria-pressed', String(control.textContent.includes('♥') || control.classList.contains('on') || /\bSaved\b/.test(control.textContent))); }
      });
      all(card, 'a[target="_blank"]').forEach(link => {
        const tokens = new Set((link.getAttribute('rel') || '').split(/\s+/));
        tokens.add('noopener'); tokens.add('noreferrer'); link.rel = [...tokens].filter(Boolean).join(' ');
      });
      if (!card.dataset.wtmapDecorated) {
        card.dataset.wtmapDecorated = '1';
        all(card, ':scope > p').forEach(p => {
          if (p.querySelector('b,strong') && /^(WiserTrail tip|Tip|Before you go|Practical tip)\s*:/i.test(p.textContent.trim())) p.classList.add('wtmap-tip');
        });
      }
    });
    all(root, 'input[type="search"]').forEach(input => {
      if (!input.hasAttribute('aria-label') && (!input.labels || !input.labels.length)) input.setAttribute('aria-label', input.placeholder || text.search || 'Search places');
    });
    all(root, '.chips button[data-cat]').forEach(b => {
      if (!b.hasAttribute('aria-pressed') || b.dataset.wtmapPressed) { b.dataset.wtmapPressed='1'; b.setAttribute('aria-pressed', String(b.classList.contains('on'))); }
    });
  }
  function initialise(root) {
    if (roots.has(root) || !own(root, '.workspace,.library,#cards,#results,.wt-free-workspace,#wtp-daylist')) return;
    roots.add(root);
    const doc = root.ownerDocument, view = doc.defaultView;
    stylesheet(doc); root.classList.add('wtmap-polished'); root.dataset.wtmapVersion = '20261020-r1';
    const mapTarget = own(root, '#map,[data-map],.wt-free-map-panel');
    const listTarget = own(root, '#cards,#results,[data-list],.wt-free-cards');
    const planTarget = own(root, '[data-tab="trip"],#opentrip,[data-tripopen],[data-member-view="trip"]');
    const planBody = own(root, '#tripbody,#triplist,[data-tripbody],#wtp-daylist,[data-days]');
    const anchor = own(root, '#mapmain .intro,.hero,.intro,.wt-free-intro,.library');
    const tools = make(doc, 'nav', 'wtmap-tools'); tools.setAttribute('aria-label', text.tools || 'Map tools');
    const activateAtlas = () => {
      if (mapTarget?.closest('[hidden]')) {
        own(root, '[data-tab="atlas"],[data-member-view="map"]')?.click();
      }
    };
    if (listTarget) tools.appendChild(button(doc, text.places, () => { activateAtlas(); focusAndScroll(listTarget); }));
    if (mapTarget) tools.appendChild(button(doc, text.map, () => { activateAtlas(); focusAndScroll(mapTarget.closest('.mapbox,.mapwrap,.wt-free-map-panel') || mapTarget); }));
    if (planTarget) tools.appendChild(button(doc, text.plan, () => { planTarget.click(); if (!planBody?.closest('dialog')) focusAndScroll(planBody); }));
    const search = own(root, '#search,[data-search],[data-search-input]');
    if (search) tools.appendChild(button(doc, text.clear, () => {
      search.value = ''; search.dispatchEvent(new view.Event('input',{bubbles:true})); search.focus({preventScroll:true});
    }));
    const help = make(doc, 'details');
    help.appendChild(make(doc, 'summary', '', text.help));
    const helpContent = make(doc, 'div', 'wtmap-help');
    [text.help_one, text.help_two, text.help_three].filter(Boolean).forEach(line => helpContent.appendChild(make(doc, 'p', '', line)));
    help.appendChild(helpContent); tools.appendChild(help);
    if (anchor?.classList.contains('library')) anchor.before(tools); else if (anchor) anchor.after(tools); else root.prepend(tools);
    let status = null, timer;
    const announce = message => {
      if (!status) { status = make(doc,'div','wtmap-status'); status.setAttribute('role','status'); status.setAttribute('aria-live','polite'); root.appendChild(status); }
      status.textContent = message; status.hidden = false; clearTimeout(timer); timer = setTimeout(() => { status.hidden = true; },4200);
    };
    function plainPlan(target) {
      const clone = target.cloneNode(true);
      all(clone, 'button,select,input,textarea,script,style,[hidden]').forEach(n => n.remove());
      all(clone, 'h1,h2,h3,h4,p,article,section,div,li').forEach(n => n.appendChild(doc.createTextNode('\n')));
      const lines = [own(root, '#triptitle,#trip-title')?.value || doc.title || text.plan, clone.textContent.replace(/[ \t]+\n/g,'\n').replace(/\n{3,}/g,'\n\n').trim()];
      all(root, '#tripnotes,#wtp-notes').forEach(n => { if(n.value.trim()) lines.push(n.value.trim()); });
      all(target,'textarea').forEach(n=>{if(n.value.trim())lines.push(n.value.trim());});
      return lines.filter(Boolean).join('\n\n');
    }
    let copyDialog;
    function showCopy() {
      if (!planBody || !planBody.textContent.trim()) { announce(text.empty); return; }
      if (!copyDialog) {
        copyDialog = make(doc, 'dialog', 'wtmap-copy');
        const heading = make(doc,'div','wtmap-copy-head');
        const title = make(doc,'h2','',text.copy_title); title.id = 'wtmap-copy-title-'+Math.random().toString(36).slice(2,9);
        copyDialog.setAttribute('aria-labelledby',title.id); heading.appendChild(title);
        heading.appendChild(button(doc,text.close,()=>copyDialog.close())); copyDialog.appendChild(heading);
        copyDialog.appendChild(make(doc,'p','',text.copy_help));
        const field=make(doc,'textarea');field.readOnly=true;field.setAttribute('aria-label',text.copy_title);copyDialog.appendChild(field);
        const actions=make(doc,'div','actions');
        actions.appendChild(button(doc,text.copy,async()=>{
          field.focus();field.select();
          try { if(!view.navigator.clipboard?.writeText) throw new Error('Clipboard unavailable'); await view.navigator.clipboard.writeText(field.value); announce(text.copied); }
          catch { announce(text.select_help); }
        }));
        actions.appendChild(button(doc,text.select,()=>{field.focus();field.select();announce(text.select_help);}));
        copyDialog.appendChild(actions);root.appendChild(copyDialog);
      }
      copyDialog.querySelector('textarea').value=plainPlan(planBody);
      if (!copyDialog.open) copyDialog.showModal();
    }
    if (planBody) {
      const copy = button(doc,text.copy,showCopy);copy.className='wtmap-copy-trigger';copy.dataset.wtmapCopy='1';
      planBody.before(copy);
    }
    // Preserve keyboard focus when existing applications replace their card DOM.
    root.addEventListener('click',e=>{
      const b=e.target.closest('button');if(!b)return;
      const attr=['data-save','data-add','data-trip','data-remove','data-tripremove'].find(a=>b.hasAttribute(a));
      if(!attr || !b.getAttribute(attr))return;
      const value=b.getAttribute(attr);
      view.requestAnimationFrame(()=>{
        if(!b.isConnected && (!doc.activeElement || doc.activeElement===doc.body)) {
          const replacement=all(root,`button[${attr}]`).find(n=>n.getAttribute(attr)===value);
          replacement?.focus({preventScroll:true});
        }
      });
    },true);
    // Country-map navigation must honour a documented named-place query.
    const countryNode=own(root,'#country-data');let country=null,activePlace=null;
    try { if(countryNode)country=JSON.parse(countryNode.textContent); } catch { /* Keep native behaviour. */ }
    const updateNavigation=()=>{
      if(!activePlace?.navigation_query)return;
      const mode=own(root,'#navmode')?.value || 'driving';
      const safeMode=['driving','walking','transit'].includes(mode)?mode:'driving';
      const google=own(root,'#google'),apple=own(root,'#apple');
      if(google)google.href='https://www.google.com/maps/dir/?'+new URLSearchParams({api:'1',destination:activePlace.navigation_query,travelmode:safeMode});
      if(apple)apple.href='https://maps.apple.com/?'+new URLSearchParams({daddr:activePlace.navigation_query,dirflg:{driving:'d',walking:'w',transit:'r'}[safeMode]});
    };
    if(Array.isArray(country?.places)) {
      root.addEventListener('click',e=>{
        const b=e.target.closest('[data-nav]');if(!b)return;
        activePlace=country.places.find(p=>p.id===b.dataset.nav)||null;
        view.setTimeout(updateNavigation,0);
      });
      own(root,'#navmode')?.addEventListener('change',()=>view.setTimeout(updateNavigation,0));
    }
    decorate(root);
    let scheduled=false;
    const observer=new view.MutationObserver(()=>{
      if(scheduled)return;scheduled=true;
      view.requestAnimationFrame(()=>{scheduled=false;decorate(root);});
    });
    all(root,'#cards,#results,[data-list],.wt-free-cards,.chips').forEach(el=>observer.observe(el,{childList:true,subtree:true}));
  }
  function inspectFrame(frame) {
    if(frames.has(frame))return;frames.add(frame);
    const run=()=>{
      try {
        const doc=frame.contentDocument;if(!doc?.body)return;
        if(doc.querySelector('#map') && doc.querySelector('#cards,#results')) initialise(doc.body);
        scan(doc);
        if(doc.querySelector('.wtmap-polished')){stylesheet(frame.ownerDocument);frame.classList.add('wtmap-frame');}
      } catch { /* Cross-origin frames and access gates are never bypassed. */ }
    };
    frame.addEventListener('load',run);run();
  }
  function scan(doc) {
    all(doc,rootSelectors).forEach(initialise);
    all(doc,'iframe').forEach(inspectFrame);
    if(!documents.has(doc)) {
      documents.add(doc);
      const view=doc.defaultView;
      const observer=new view.MutationObserver(records=>{
        records.forEach(record=>record.addedNodes.forEach(n=>{
          if(n.nodeType!==1)return;
          if(n.matches('iframe'))inspectFrame(n);
          all(n,'iframe').forEach(inspectFrame);
          if(n.matches(rootSelectors))initialise(n);
          all(n,rootSelectors).forEach(initialise);
        }));
      });
      observer.observe(doc.body,{childList:true,subtree:true});
    }
  }
  window.WTMapPolish = {version:'20261020-r1',scan:()=>scan(document)};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>scan(document),{once:true});else scan(document);
  document.addEventListener('shopify:section:load',()=>scan(document));
})();

